import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, Loader2, MapPin, Play, Square, Star, Tent, Trash2, Waves, XCircle } from 'lucide-react';
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../services/firebaseService';
import { NORDIC_PRESCAN_GROUPS, generateGrid, type RegionBounds } from '../../../utils/prescanRegions';

const GOOGLE_CELL_LAT = 0.15;
const GOOGLE_CELL_LON = 0.28;
const SCAN_DELAY_MS = 1000;

interface RegionScanStatus {
  status: 'in_progress' | 'completed' | 'cancelled';
  startedAt: string;
  completedAt: string | null;
  cellsTotal: number;
  cellsCompleted: number;
  newCandidates: number;
  foundTotal?: number;
  enrichedTotal?: number;
}

interface GoogleScanStats {
  lastRunAt: string | null;
  totalTilesScanned: number;
  totalFound: number;
  totalNew: number;
  totalEnriched: number;
  regions?: Record<string, RegionScanStatus>;
}

interface GoogleCandidate {
  placeId: string;
  name: string;
  location: { latitude: number; longitude: number };
  types: string[];
  vicinity: string;
  rating: number | null;
  userRatingsTotal: number | null;
  status: 'auto_added' | 'pending' | 'rejected';
  detectedAs: 'campground' | 'bathing';
  foundAt: number;
  addedSpotId?: string;
}

const AdminGoogleScanPanel: React.FC = () => {
  const [stats, setStats] = useState<GoogleScanStats | null>(null);
  const [autoAdded, setAutoAdded] = useState<GoogleCandidate[]>([]);
  const [scanningRegion, setScanningRegion] = useState<string | null>(null);
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0 });
  const [openCountries, setOpenCountries] = useState<Record<string, boolean>>({ Norge: true });
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState<{ processed: number; added: number; enriched: number } | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'system', 'google_scan_status'), snap => {
      setStats((snap.data() as GoogleScanStats) || null);
    }, () => {});
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'google_places_candidates'),
      where('status', '==', 'auto_added'),
      orderBy('foundAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(q, snap => {
      setAutoAdded(snap.docs.map(d => ({ placeId: d.id, ...d.data() } as GoogleCandidate)));
    }, () => {});
    return unsub;
  }, []);

  const clearTileCache = async () => {
    const snap = await getDocs(collection(db, 'google_tiles'));
    if (snap.empty) return;
    const BATCH_SIZE = 500;
    for (let i = 0; i < snap.docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      snap.docs.slice(i, i + BATCH_SIZE).forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  };

  const statusDocRef = doc(db, 'system', 'google_scan_status');

  const startScan = async (region: RegionBounds) => {
    const grid = generateGrid(region, GOOGLE_CELL_LAT, GOOGLE_CELL_LON);
    setScanningRegion(region.name);
    setScanProgress({ current: 0, total: grid.length });
    cancelledRef.current = false;

    const startedAt = new Date().toISOString();
    await updateDoc(statusDocRef, {
      [`regions.${region.name}`]: { status: 'in_progress', startedAt, completedAt: null, cellsTotal: grid.length, cellsCompleted: 0, newCandidates: 0 },
    }).catch(() => setDoc(statusDocRef, {
      [`regions.${region.name}`]: { status: 'in_progress', startedAt, completedAt: null, cellsTotal: grid.length, cellsCompleted: 0, newCandidates: 0 },
    }, { merge: true }));

    const scanFn = httpsCallable(functions, 'scanGooglePlacesCandidates', { timeout: 180000 });
    let totalNew = 0;
    let totalFound = 0;
    let totalEnriched = 0;

    for (let i = 0; i < grid.length; i++) {
      if (cancelledRef.current) break;
      setScanProgress({ current: i + 1, total: grid.length });
      try {
        const result = await scanFn({ bounds: grid[i] });
        totalNew += (result.data as any)?.newCount ?? 0;
        totalFound += (result.data as any)?.foundCount ?? 0;
        totalEnriched += (result.data as any)?.enrichCount ?? 0;
      } catch (e) {
        console.error(`Google scan cell ${i} feilet:`, e);
      }
      if (i < grid.length - 1 && !cancelledRef.current) {
        await new Promise(r => setTimeout(r, SCAN_DELAY_MS));
      }
    }

    const completedAt = new Date().toISOString();
    const finalStatus = cancelledRef.current ? 'cancelled' : 'completed';
    await updateDoc(statusDocRef, {
      [`regions.${region.name}.status`]: finalStatus,
      [`regions.${region.name}.completedAt`]: completedAt,
      [`regions.${region.name}.cellsCompleted`]: grid.length,
      [`regions.${region.name}.newCandidates`]: totalNew,
      [`regions.${region.name}.foundTotal`]: totalFound,
      [`regions.${region.name}.enrichedTotal`]: totalEnriched,
    }).catch(() => {});

    setScanningRegion(null);
    setScanProgress({ current: 0, total: 0 });
  };

  const cancelScan = () => {
    cancelledRef.current = true;
  };

  const removeSpot = async (candidate: GoogleCandidate) => {
    if (!candidate.addedSpotId) return;
    setRemovingId(candidate.placeId);
    try {
      await updateDoc(doc(db, 'cached_spots', candidate.addedSpotId), {
        isInvalid: true,
        invalidReason: 'Fjernet etter Google-import',
      });
      await updateDoc(doc(db, 'google_places_candidates', candidate.placeId), {
        status: 'rejected',
        reviewedAt: Date.now(),
      });
    } finally {
      setRemovingId(null);
    }
  };

  const migratePending = async () => {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const fn = httpsCallable(functions, 'processPendingGoogleCandidates', { timeout: 300000 });
      const result = await fn({});
      const d = result.data as any;
      setMigrateResult({ processed: d.processed, added: d.added, enriched: d.enriched });
    } finally {
      setMigrating(false);
    }
  };

  const toggleCountry = (country: string) => {
    setOpenCountries(prev => ({ ...prev, [country]: !prev[country] }));
  };

  return (
    <div className="space-y-6 pb-8">

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Tiles scannet', value: stats?.totalTilesScanned ?? 0 },
          { label: 'Google-treff', value: stats?.totalFound ?? 0 },
          { label: 'Enriched OSM', value: stats?.totalEnriched ?? 0 },
          { label: 'Nye steder', value: stats?.totalNew ?? 0 },
        ].map(s => (
          <div key={s.label} className="bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-xl p-3 text-center">
            <div className="text-xl font-bold text-[var(--text-primary)]">{s.value.toLocaleString('nb-NO')}</div>
            <div className="text-xs text-[var(--text-muted)] mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Info-boks */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-xs text-blue-300 space-y-1">
        <div className="font-medium text-blue-200">Automatisk harmonisering</div>
        <div>Treff innenfor 300m av eksisterende OSM-sted → <span className="text-blue-200">enriches</span> med Google Place ID (bilder lastes på-forespørsel).</div>
        <div>Genuint nye steder → legges <span className="text-blue-200">direkte på kartet</span>. Du kan fjerne dårlige treff nedenfor.</div>
      </div>

      {/* Migrer gamle pending-kandidater */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-center justify-between gap-3">
        <div className="text-xs text-amber-200">
          <div className="font-medium">Gamle scan-resultater</div>
          <div className="text-amber-300/80 mt-0.5">
            {migrateResult
              ? `Ferdig: ${migrateResult.processed} behandlet · ${migrateResult.added} lagt til · ${migrateResult.enriched} enriched`
              : 'Kandidater fra tidligere skanninger (status: pending) er ikke lagt på kartet ennå.'}
          </div>
        </div>
        <button
          onClick={migratePending}
          disabled={migrating}
          className="flex items-center gap-1.5 text-xs text-amber-400 border border-amber-500/30 hover:bg-amber-500/10 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {migrating ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
          {migrating ? 'Behandler...' : 'Behandle nå'}
        </button>
      </div>

      {/* Tøm tile-cache */}
      <div className="flex justify-end">
        <button
          onClick={clearTileCache}
          className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-red-400 border border-[var(--border-primary)] hover:border-red-500/30 px-3 py-1.5 rounded-lg transition-colors"
        >
          <Trash2 size={12} />
          Tøm tile-cache (tving ny scan)
        </button>
      </div>

      {/* Pågående scan */}
      {scanningRegion && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Loader2 size={16} className="text-amber-500 animate-spin" />
            <div>
              <div className="text-sm font-medium text-amber-500">{scanningRegion}</div>
              <div className="text-xs text-[var(--text-muted)]">
                Celle {scanProgress.current} / {scanProgress.total}
              </div>
            </div>
          </div>
          <button onClick={cancelScan} className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 transition-colors">
            <Square size={12} />
            Stopp
          </button>
        </div>
      )}

      {/* Region-seksjoner */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide px-1">Scan region</h3>
        {NORDIC_PRESCAN_GROUPS.map(group => (
          <div key={group.country} className="bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-xl overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-hover)] transition-colors"
              onClick={() => toggleCountry(group.country)}
            >
              <span className="text-sm font-medium text-[var(--text-primary)]">{group.country}</span>
              {openCountries[group.country] ? <ChevronDown size={16} className="text-[var(--text-muted)]" /> : <ChevronRight size={16} className="text-[var(--text-muted)]" />}
            </button>

            {openCountries[group.country] && (
              <div className="border-t border-[var(--border-primary)] divide-y divide-[var(--border-primary)]">
                {group.regions.map(region => {
                  const isScanning = scanningRegion === region.name;
                  const cellCount = generateGrid(region, GOOGLE_CELL_LAT, GOOGLE_CELL_LON).length;
                  const rs = stats?.regions?.[region.name];
                  const completedAt = rs?.status === 'completed' && rs.completedAt
                    ? new Date(rs.completedAt).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })
                    : null;
                  return (
                    <div key={region.name} className="flex items-center justify-between px-4 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-[var(--text-primary)]">{region.name}</span>
                        <span className="text-xs text-[var(--text-muted)]">{cellCount} celler</span>
                        {completedAt && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
                            <CheckCircle2 size={10} />
                            {completedAt}
                            {(rs.foundTotal ?? 0) > 0 && <span className="text-emerald-300">· {rs.foundTotal} treff</span>}
                            {(rs.enrichedTotal ?? 0) > 0 && <span className="text-blue-300">· {rs.enrichedTotal} enriched</span>}
                            {rs.newCandidates > 0 && <span className="text-emerald-300">· {rs.newCandidates} nye</span>}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => isScanning ? cancelScan() : startScan(region)}
                        disabled={!!scanningRegion && !isScanning}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors flex-shrink-0 ${
                          isScanning
                            ? 'text-red-400 border-red-500/20 hover:bg-red-500/10'
                            : 'text-green-400 border-green-500/20 hover:bg-green-500/10 disabled:opacity-40 disabled:cursor-not-allowed'
                        }`}
                      >
                        {isScanning ? <><Square size={11} /> Stopp</> : <><Play size={11} /> Scan</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Auto-lagt til */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide px-1">
          Auto-lagt til på kartet
          {autoAdded.length > 0 && (
            <span className="ml-2 bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded-full font-normal">{autoAdded.length}</span>
          )}
        </h3>

        {autoAdded.length === 0 ? (
          <div className="text-center py-10 text-[var(--text-muted)] text-sm">
            Ingen auto-lagt til ennå — kjør en region-scan.
          </div>
        ) : (
          <div className="space-y-2">
            {autoAdded.map(c => (
              <div key={c.placeId} className="bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-xl p-3 flex items-start gap-3">
                <div className={`mt-0.5 p-1.5 rounded-lg flex-shrink-0 ${c.detectedAs === 'campground' ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'}`}>
                  {c.detectedAs === 'campground' ? <Tent size={14} /> : <Waves size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[var(--text-primary)] truncate">{c.name}</span>
                    {c.rating && (
                      <span className="flex items-center gap-0.5 text-xs text-amber-400">
                        <Star size={10} fill="currentColor" />
                        {c.rating.toFixed(1)}
                        {c.userRatingsTotal && <span className="text-[var(--text-muted)]"> ({c.userRatingsTotal})</span>}
                      </span>
                    )}
                  </div>
                  {c.vicinity && (
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-[var(--text-muted)]">
                      <MapPin size={10} />
                      <span className="truncate">{c.vicinity}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeSpot(c)}
                  disabled={removingId === c.placeId || !c.addedSpotId}
                  className="flex items-center gap-1 text-xs text-red-400 border border-red-500/20 hover:bg-red-500/10 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                >
                  {removingId === c.placeId ? <Loader2 size={11} className="animate-spin" /> : <XCircle size={11} />}
                  Fjern
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGoogleScanPanel;
